import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
} from '@angular/core';
import { IconComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { ButtonComponent } from '@kinomap-dev/ng-ui-lib/buttons';
import { PaginationMeta } from 'app/shared/models/pagination.models';

export type PaginationAppearance = {
  activeBackgroundColor: string;
  activeTextColor: string;
  borderColor: string;
  buttonBackgroundColor: string;
  buttonTextColor: string;
};

const DEFAULT_PAGINATION_APPEARANCE: PaginationAppearance = {
  activeBackgroundColor: 'var(--primary-color, #1a1918)',
  activeTextColor: '#1a1918',
  borderColor: '#d6d6d6',
  buttonBackgroundColor: '#ffffff',
  buttonTextColor: '#1a1918',
};

type PaginationItem = {
  id: string;
  label: string;
  page: number;
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--pagination-active-bg]': 'appearance().activeBackgroundColor',
    '[style.--pagination-active-text]': 'appearance().activeTextColor',
    '[style.--pagination-border-color]': 'appearance().borderColor',
    '[style.--pagination-button-bg]': 'appearance().buttonBackgroundColor',
    '[style.--pagination-button-text]': 'appearance().buttonTextColor',
  },
  imports: [ButtonComponent, IconComponent],
  selector: 'app-pagination',
  styleUrl: './pagination.component.scss',
  templateUrl: './pagination.component.html',
})
export class PaginationComponent {
  readonly appearance: InputSignal<PaginationAppearance> = input<PaginationAppearance>(
    DEFAULT_PAGINATION_APPEARANCE
  );
  readonly meta: InputSignal<PaginationMeta | null> = input<PaginationMeta | null>(null);
  readonly pageChange: OutputEmitterRef<number> = output<number>();
  readonly navigationIconColor: Signal<string> = computed(() => this.appearance().buttonTextColor);

  readonly pages: Signal<PaginationItem[]> = computed(() => {
    const meta: PaginationMeta | null = this.meta();
    if (!meta || meta.last_page <= 1) return [];

    const firstPage: number = 1;
    const lastPage: number = meta.last_page;
    const currentPage: number = meta.current_page;
    const windowStart: number = Math.max(firstPage, currentPage - 1);
    const windowEnd: number = Math.min(lastPage, currentPage + 1);
    const pages: PaginationItem[] = [];

    for (let page: number = windowStart; page <= windowEnd; page += 1) {
      pages.push({
        id: `page-${page}`,
        label: String(page),
        page,
      });
    }

    return pages;
  });

  get currentPage(): number {
    return this.meta()?.current_page ?? 1;
  }

  get lastPage(): number {
    return this.meta()?.last_page ?? 1;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.lastPage || page === this.currentPage) return;

    this.pageChange.emit(page - 1);
  }
}
